/**
 * @license
 * Copyright (c) 2017, Barclays Africa Technology, All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file
 */
import { Observable } from 'rxjs/Observable';
import { Action } from '@ngrx/store';
import * as actions from '../actions/actions';
import { LocalizationPackage, LocalizationPackageAttributes } from '../../models/localization';

/**
 * ThresholdState
 */
export interface ThresholdState {
    applicationTitle: string,
    bannerClass: string;
    bannerBackground: string,
    bannerTitle: string,
    bannerCaption: string,
    bannerAlign: string,
    showContextNav: boolean;
    showBanner: boolean;
    showMenu: boolean;
    contextNav: any;
    showProfileIcon: boolean;
    mainMenu: any;
    breadcrumb: any;
    showMainMenu: boolean;
    color: string;
    variant: string;
    size: string;
    showSearchControl: boolean;
    showSearchBar: boolean;
    dynamicNav: any;
    showDynamicSmallMenuWithNoChildren: boolean;
    localizationDefaultLang: string;
    localizationPackages: any[];
    localizationSupportedLangs: string[];
    loadingTranslations: boolean;
    activityFeature: boolean;
    showHeaderMasthead: boolean;
    allowExternalNavigation: boolean;
    containerClass: string;
};

/**
 * User default state
 */
export const initialState: ThresholdState = {
    applicationTitle: '',
    bannerClass: 'sol-header', // default required
    bannerBackground: '',
    bannerTitle: '',
    bannerCaption: '',
    bannerAlign: 'bannerDefault',
    showProfileIcon: false,
    showContextNav: false,
    showBanner: false,
    showMenu: false,
    contextNav: [],
    mainMenu: [],
    breadcrumb: [],
    showMainMenu: false,
    color: '',
    variant: '',
    size: 'sol-jumbo-h1',
    showSearchControl: false,
    showSearchBar: false,
    dynamicNav: '',
    showDynamicSmallMenuWithNoChildren: false,
    localizationDefaultLang: '',
    localizationPackages: [],
    localizationSupportedLangs: [],
    loadingTranslations: false,
    activityFeature: true,
    showHeaderMasthead: true,
    allowExternalNavigation: false,
    containerClass: 'threshold-container'
};

/**
 * The 'info' reducer
 * @param state The current state
 * @param action The action we use to change the state
 * @return The new instance of UserState
 */
export function reducer(state: ThresholdState = initialState, action: actions.Actions): ThresholdState {
    switch (action.type) {

        case actions.SET_COLOR:
            return { ...state, color: action.payload };

        case actions.SET_VARIANT:
            return { ...state, variant: action.payload };

        case actions.SET_SIZE:
            return { ...state, size: action.payload };

        case actions.SHOW_MAIN_MENU:
            return { ...state, showMainMenu: true };

        case actions.HIDE_MAIN_MENU:
            return { ...state, showMainMenu: false };

        case actions.SET_MAIN_MENU:
            return { ...state, mainMenu: [...action.payload] };

        case actions.SET_BREADCRUMB:
                return { ...state, breadcrumb: [...action.payload] };            

        case actions.SHOW_CONTEXT_NAV:
            return { ...state, showContextNav: true };

        case actions.HIDE_CONTEXT_NAV:
            return { ...state, showContextNav: false };

        case actions.SHOW_BANNER:
            return { ...state, showBanner: true }

        case actions.HIDE_BANNER:
            return { ...state, showBanner: false };

        case actions.SET_CONTEXT_NAV:
            return { ...state, contextNav: [...action.payload] };

        case actions.HIDE_BANNER_PROFILE:
            return { ...state, showProfileIcon: false };

        case actions.SHOW_BANNER_PROFILE:
            return { ...state, showProfileIcon: true };

        case actions.SET_APPLICATION_TITLE:
            return { ...state, applicationTitle: action.payload };

        case actions.SET_BANNER_CLASS:
                return { ...state, bannerClass: action.payload };            

        case actions.SET_BANNER_BACKGROUND:
            return { ...state, bannerBackground: action.payload };

        case actions.SET_BANNER_TITLE:
            return { ...state, bannerTitle: action.payload };

        case actions.SET_BANNER_CAPTION:
            return { ...state, bannerCaption: action.payload };

        case actions.SET_BANNER_ALIGN:
            return { ...state, bannerAlign: action.payload };

        case actions.SHOW_MENU:
            return { ...state, showMenu: true };

        case actions.HIDE_MENU:
            return { ...state, showMenu: false };

        case actions.SET_DYNAMIC_MENU:
            return { ...state, dynamicNav: [...action.payload] };

        case actions.SHOW_SEARCH_CONTROL:
            return { ...state, showSearchControl: true };

        case actions.HIDE_SEARCH_CONTROL:
            return { ...state, showSearchControl: false };

        case actions.TOGGLE_SEARCH_BAR:
            return { ...state, showSearchBar: !state.showSearchBar };

        case actions.SHOW_DYNAMIC_SMALL_MENU_WITH_NO_CHILDREN:
            return { ...state, showDynamicSmallMenuWithNoChildren: true }

        case actions.HIDE_DYNAMIC_SMALL_MENU_WITH_NO_CHILDREN:
            return { ...state, showDynamicSmallMenuWithNoChildren: false }
            
        case actions.ADD_LOCALIZATION_PACKAGE:              

            let currentState = state.localizationPackages; // current language state
            let finalPayload;            

            if (currentState.length === 0) {

                finalPayload = [ action.payload ];
                finalPayload = [
                    {
                        languageCode: action.payload['languageCode'],
                        attributes: [
                            action.payload['attributes']
                        ]
                    }
                ]                
            } else {
                let newLanguage: boolean = true;
                for (let i = 0; i < currentState.length; i++) {
                    if (currentState[i].languageCode === action.payload['languageCode']) {                    
                        // append to language attributes
                        currentState[i].attributes.push(action.payload['attributes'])
                        newLanguage = false;
                    }                    
                }

                if (newLanguage) {                       
                    currentState.push(
                        {
                            languageCode: action.payload['languageCode'],
                            attributes: [ action.payload['attributes'] ]
                        }
                    );
                }
                finalPayload = currentState;
            }
            
            return { ...state, localizationPackages: finalPayload };            

        case actions.SET_LOCALIZATION_DEFAULT_LANGUAGE:
            if ( state.localizationSupportedLangs.indexOf(action.payload) == -1 )  {
                return { ...state, localizationDefaultLang: state.localizationDefaultLang };
            } else {
                return { ...state, localizationDefaultLang: action.payload };            
            }

            case actions.SET_LOCALIZATION_SUPPORTED_LANGUAGES:
                return { ...state, localizationSupportedLangs: action.payload };    

            case actions.LOADING_LANGUAGE:
                return { ...state, loadingTranslations: true };    

            case actions.LOADING_LANGUAGE_SUCCESS:
                return { ...state, loadingTranslations: false };     
                
        case actions.SET_USER_ACTIVITY:
                return { ...state, activityFeature: action.payload };        
                
        case actions.SHOW_HEADER_MASTHEAD:
                return { ...state, showHeaderMasthead: true };

        case actions.HIDE_HEADER_MASTHEAD:
                return { ...state, showHeaderMasthead: false };          
                        
        case actions.SET_ALLOW_EXTERNAL_NAVIGATION:
                return { ...state, allowExternalNavigation: action.payload };                

        case actions.SET_CONTAINER_CLASS:
                    return { ...state, containerClass: action.payload };                  

        default:
            return state;
    }
}

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */

export const getBannerClass = (state: ThresholdState) => state.bannerClass;
export const getBackgroundBanner = (state: ThresholdState) => state.bannerBackground;
export const getShowBanner = (state: ThresholdState) => state.showBanner;
export const getShowMenu = (state: ThresholdState) => state.showMenu;
export const getApplicationTitle = (state: ThresholdState) => state.applicationTitle;
export const getBannerTitle = (state: ThresholdState) => state.bannerTitle;
export const getBannerCaption = (state: ThresholdState) => state.bannerCaption;
export const getShowContextNav = (state: ThresholdState) => state.showContextNav;
export const getContextNav = (state: ThresholdState) => state.contextNav;
export const getShowProfileIcon = (state: ThresholdState) => state.showProfileIcon;
export const getBannerAlign = (state: ThresholdState) => state.bannerAlign;
export const getShowMainMenu = (state: ThresholdState) => state.showMainMenu;
export const getMainMenu = (state: ThresholdState) => state.mainMenu;
export const getBreadcrumb = (state: ThresholdState) => state.breadcrumb;
export const getColor = (state: ThresholdState) => state.color;
export const getVariant = (state: ThresholdState) => state.variant;
export const getSize = (state: ThresholdState) => state.size;
export const getShowSearchControl = (state: ThresholdState) => state.showSearchControl;
export const getShowSearchBar = (state: ThresholdState) => state.showSearchBar;
export const getDynamicNav = (state: ThresholdState) => state.dynamicNav;
export const getShowDynamicSmallMenuWithNoChildren = (state: ThresholdState) => state.showDynamicSmallMenuWithNoChildren;
export const getLocalizationPackages = (state: ThresholdState) => state.localizationPackages;
export const getLocalizationDefaultLang = (state: ThresholdState) => state.localizationDefaultLang;
export const getLocalizationLoading = (state: ThresholdState) => state.loadingTranslations;
export const getActivityFeature = (state: ThresholdState) => state.activityFeature;
export const getShowHeaderMasthead = (state: ThresholdState) => state.showHeaderMasthead;
export const getAllowExternalNavigation = (state: ThresholdState) => state.allowExternalNavigation;
export const getContainerClass = (state: ThresholdState) => state.containerClass;



// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/threshold/store/reducers/reducer.ts